import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Nav from "./Nav";
import Footer from "./Footer";
import QuickView from "./QuickView";
import Row from "./Row";
import "./SeriesWatch.scss";
import { useAuth } from "../context/AuthContext";
import {
  buildMediaUrl,
  fetchTitleDetails,
  getDirectPlayPath,
} from "../services/streamApi";

function SeriesWatch() {
  const { id } = useParams();
  const { user } = useAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [selectedTitle, setSelectedTitle] = useState(null);

  useEffect(() => {
    let active = true;

    async function loadTitle() {
      try {
        setLoading(true);
        const data = await fetchTitleDetails(id, user?.userId);
        if (!active) return;
        setPayload(data);
        setSelectedSeason(data?.seasons?.[0]?.seasonNumber || 1);
      } catch (error) {
        console.error("Erreur detail titre:", error);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadTitle();
    return () => {
      active = false;
    };
  }, [id, user?.userId]);

  const title = payload?.title;
  const seasons = payload?.seasons || [];
  const isSerialized = title?.type === "serie" || title?.type === "documentaire";
  const activeSeason = seasons.find((season) => season.seasonNumber === selectedSeason) || seasons[0] || null;
  const firstEpisode = seasons.flatMap((season) => season.episodes)[0] || null;
  const totalEpisodes = seasons.reduce((total, season) => total + season.episodes.length, 0);
  const detailFacts = [
    title?.type ? { label: "Format", value: title.type === "serie" ? "Serie" : title.type === "documentaire" ? "Documentaire" : "Film" } : null,
    title?.categoryLabel ? { label: "Genre", value: title.categoryLabel } : null,
    title?.audioLanguages?.length ? { label: "Audio", value: title.audioLanguages.join(", ") } : title?.audio ? { label: "Audio", value: title.audio } : null,
    title?.subtitles?.length ? { label: "Sous-titres", value: title.subtitles.join(", ") } : null,
    title?.maturity ? { label: "Classification", value: title.maturity } : null,
    title?.availableQualities?.length ? { label: "Qualite", value: title.availableQualities.join(" / ") } : title?.badges?.length ? { label: "Qualite", value: title.badges.join(" / ") } : null,
    isSerialized ? { label: "Saisons", value: seasons.length || 0 } : null,
    isSerialized ? { label: "Episodes", value: totalEpisodes } : null,
  ].filter(Boolean);
  const continueCopy =
    title?.type === "documentaire"
      ? "Reprenez cette collection documentaire la ou vous vous etiez arretes, puis naviguez saison par saison."
      : "Reprenez cette serie la ou vous vous etiez arretes, puis naviguez saison par saison.";
  const canPlayEpisode = Boolean(firstEpisode);
  const canStartPlayback = title?.type === "film" || canPlayEpisode;
  const primaryPlayPath = canPlayEpisode
    ? getDirectPlayPath(title, firstEpisode.id)
    : getDirectPlayPath(title);

  return (
    <div className="app-shell">
      <Nav />

      <main className="title-page">
        {loading && !payload ? (
          <section className="page-placeholder">
            <div className="page-placeholder__row" />
            <div className="page-placeholder__row" />
          </section>
        ) : null}

        <section
          className="title-page__hero"
          style={{
            "--title-bg": `url("${buildMediaUrl(title?.backdropPath || title?.posterPath)}")`, 
          }}
        >
          <div className="title-page__overlay" />
          <div className="title-page__heroContent">
            <p className="title-page__eyebrow">{title?.type === "film" ? "Film" : title?.type === "serie" ? "Serie" : "Documentaire"}</p>
            <h1>{title?.title || "Chargement..."}</h1>

            <div className="title-page__meta">
              {title?.year ? <span>{title.year}</span> : null}
              {title?.categoryLabel ? <span>{title.categoryLabel}</span> : null}
              {title?.durationLabel ? <span>{title.durationLabel}</span> : null}
              {title?.maturity ? <span>{title.maturity}</span> : null}

              {title?.views ? <span>{title.views || 0} vues</span> : null}
              {title?.likes ? <span>{title.likes || 0} likes</span> : null}
              
            </div>

            <p className="title-page__description">
              {title?.description ||
                "Interface detaillee avec saisons, episodes, lecture continue et recommandations."}
            </p>

            <div className="title-page__actions">
              {title && canStartPlayback ? (
                <Link to={primaryPlayPath} className="title-page__button title-page__button--play">
                  <PlayArrowIcon /> {title.resume ? "Reprendre" : "Lecture"}
                </Link>
              ) : title ? (
                <button className="title-page__button title-page__button--play" disabled>
                  <PlayArrowIcon /> Episodes bientot
                </button>
              ) : null}
              <button className="title-page__button" onClick={() => setSelectedTitle(title)}>
                <InfoOutlinedIcon /> Plus d&apos;infos
              </button>
            </div>

            {title?.resume ? (
              <div className="title-page__resume">
                <span>Reprise a {title.resume.percent}%</span>
                <div className="title-page__resumeBar">
                  <span style={{ width: `${title.resume.percent}%` }} />
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <section className="title-page__details">
          <div className="title-page__infoCard">
            <h2>A propos de ce titre</h2>
            <p>{title?.description}</p>
            <div className="title-page__tags">
              {title?.badges?.map((badge) => (
                <span key={badge}>{badge}</span>
              ))}
              {title?.audio ? <span>Audio {title.audio}</span> : null}
              {title?.subtitles?.length ? <span>Sous-titres {title.subtitles.join(", ")}</span> : null}
            </div>
            {detailFacts.length ? (
              <div className="title-page__facts">
                {detailFacts.map((fact) => (
                  <div key={fact.label}>
                    <span>{fact.label}</span>
                    <strong>{fact.value}</strong>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {isSerialized ? (
            <div className="title-page__infoCard">
              <h2>Lecture continue</h2>
              <p className="title-page__infoText">{continueCopy}</p>
              <div className="title-page__metrics">
                <div>
                  <strong>{seasons.length || 0}</strong>
                  <span>saisons</span>
                </div>
                <div>
                  <strong>{totalEpisodes}</strong>
                  <span>episodes</span>
                </div>
                <div>
                  <strong>{title?.views || 0}</strong>
                  <span>lectures</span>
                </div>
                <div>
                  <strong>{title?.likes || 0}</strong>
                  <span>likes</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="title-page__infoCard">
              <h2>Signaux plateforme</h2>
              <div className="title-page__metrics">
                <div>
                  <strong>{title?.views || 0}</strong>
                  <span>lectures</span>
                </div>
                <div>
                  <strong>{title?.likes || 0}</strong>
                  <span>likes</span>
                </div>
                <div>
                  <strong>{title?.matchScore || 0}%</strong>
                  <span>match</span>
                </div>
                <div>
                  <strong>{title?.categoryLabel || title?.type || "-"}</strong>
                  <span>categorie</span>
                </div>
              </div>
            </div>
          )}
        </section>

        {seasons.length ? (
          <section className="title-page__episodes">
            <div className="title-page__sectionHeader">
              <div>
                <h2>Lecture continue</h2>
                <p className="title-page__sectionIntro">{continueCopy}</p>
              </div>
            </div>

            <div className="title-page__seasonLayout">
              <aside className="title-page__seasonRail" aria-label="Saisons">
                {seasons.map((season) => (
                  <button
                    key={season.id || season.seasonNumber}
                    className={season.seasonNumber === activeSeason?.seasonNumber ? "is-active" : ""}
                    onClick={() => setSelectedSeason(season.seasonNumber)}
                  >
                    <span>Saison {season.seasonNumber}</span>
                    <small>{season.episodes.length} episode{season.episodes.length > 1 ? "s" : ""}</small>
                  </button>
                ))}
              </aside>

              <div className="title-page__seasonPanel">
                <div className="title-page__seasonTitle">
                  <span>Saison {activeSeason?.seasonNumber}</span>
                  <small>
                    {activeSeason?.episodes?.length || 0} episode{(activeSeason?.episodes?.length || 0) > 1 ? "s" : ""}
                  </small>
                </div>

                <div className="title-page__episodeList">
                  {activeSeason?.episodes?.map((episode) => (
                    <article key={episode.id} className="title-page__episodeCard">
                      <div className="title-page__episodeMedia">
                        <img src={buildMediaUrl(episode.imagePath || title?.backdropPath)} alt={episode.title} />
                        {episode.durationLabel ? <span className="title-page__episodeDuration">{episode.durationLabel}</span> : null}
                      </div>
                      <div className="title-page__episodeBody">
                        <div className="title-page__episodeHeader">
                          <div>
                            <p>
                              S{episode.seasonNumber}:E{episode.episodeNumber}
                            </p>
                            <h3>{episode.title}</h3>
                          </div>
                          <Link to={getDirectPlayPath(title, episode.id)} className="title-page__episodePlay">
                            <PlayArrowIcon />
                          </Link>
                        </div>
                        <p>{episode.description || "Episode pret pour la lecture continue."}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <Row
          title="Vous aimerez aussi"
          subtitle="Suggestions intelligentes proches de ce titre."
          items={payload?.related || []}
          onMoreInfo={setSelectedTitle}
        />
      </main>

      <Footer />
      <QuickView movie={selectedTitle} popup={() => setSelectedTitle(null)} popupStatut={Boolean(selectedTitle)} />
    </div>
  );
}

export default SeriesWatch;
